import { useCallback, useEffect, useRef, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { toast, ToastContainer } from "react-toastify";

import ProgressBar from "../../../../shared/LinearProgressBar";

import { ASSESSMENT_ALLOWED, LMS_COURSE_ID, LMS_COURSE_NAME, LMS_TOKEN } from "../../../../utils/cookiesmanager";
import { LMS_BASE_URL } from "../../../../utils/http/http-common";

import Button from "../ButtonComponent";

import QuestionCards from "../QuestionCards";

import HeaderModule from "../ModuleHeaderComponent";

import { BUTTON_VARIANTS } from "../constant";

import MandatoryAssessmentModal from "../MandatoryAssessmentModal";

import Shimmer from "../ShimmerComponent";

import { useGetPreAssessmentQuestion } from "./queries";

import { useSavePreAssessment } from "./mutation";

import styles from "./styles.module.scss";

const getPreAssessmentPayload = ({
  courseId,
  questions = [],
  selectedAnswers = {},
}) => ({
  courseId: String(courseId),
  questionArray:
    questions
      ?.map((question) => {
        const answer = selectedAnswers[question?.assQuestionId];

        if (!answer) return null;

        return {
          assQuestionID: String(question?.assQuestionId),
          assAnswerID: String(answer?.assessmentAnswerId),
        };
      })
      .filter(Boolean) || [],
});

const PreCourseAssessment = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [animate, setAnimate] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(10 * 60);
  const [allowLeave, setAllowLeave] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const latestAssessmentRef = useRef({
    courseId: "",
    response: [],
    selectedAnswers: {},
  });

  const [searchParams] = useSearchParams();

  const languageid = localStorage.getItem("l_id") || 1;

  const courseId = searchParams.get("courseId") || localStorage.getItem(LMS_COURSE_ID);
  const courseName = searchParams.get("courseName") || localStorage.getItem(LMS_COURSE_NAME);

  const navigate = useNavigate();

  const { data: preAssessmentQuestion, isLoading } =
    useGetPreAssessmentQuestion({ courseId, languageid });

  const { mutate, isPending } = useSavePreAssessment();

  const { data: preAssessmentQuestionData } = preAssessmentQuestion || {};

  const { response } = preAssessmentQuestionData || {};

  useEffect(() => {
    latestAssessmentRef.current = {
      courseId,
      response,
      selectedAnswers,
    };
  }, [courseId, response, selectedAnswers]);

  const handleSubmit = () => {
    const currentQuestion = response?.[activeIndex];

    if (!selectedAnswers[currentQuestion?.assQuestionId]) {
      toast.error("Please select an answer");
      return;
    }

    if (isLastQuestion) {
      setShowSubmitModal(true);
      return;
    }

    setAnimate(true);

    setTimeout(() => {
      setAnimate(false);
      setActiveIndex((prev) => prev + 1);
    }, 500); // animation duration
  };

  const handleFinalSubmit = () => {
    const payload = getPreAssessmentPayload({
      courseId,
      questions: response,
      selectedAnswers,
    });

    if (payload.questionArray.length !== response?.length) {
      toast.error("Please answer all questions before submitting");
      return;
    }

    mutate(payload, {
      onSuccess(data) {
        const responseData = data?.data || data;
        const { status, error } = responseData || {};

        if (status === 0 || status === false) {
          toast.error(error || "Something went wrong");
          return;
        }

        setShowSubmitModal(false);
        navigate(
          `/user/module-list?courseId=${courseId}&courseName=${courseName}`,
        );
      },
      onError(error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        toast(errorMessage, { type: "error" });
      },
      onSettled() {},
    });
  };

  const handleReview = () => {
    setShowSubmitModal(false);
    setActiveIndex(0);
  };

  const handlePreviousClick = () => {
    if (activeIndex === 0 || activeIndex < 0) return;

    setActiveIndex((prev) => prev - 1);
  };

  const handleAutoSubmit = () => {
    navigate(
            `/user/module-list?courseId=${courseId}&courseName=${courseName}`,
      );
  };

  const submitAssessmentOnRefresh = useCallback(() => {
    const {
      courseId: latestCourseId,
      response: latestResponse,
      selectedAnswers: latestSelectedAnswers,
    } = latestAssessmentRef.current;

    const payload = getPreAssessmentPayload({
      courseId: latestCourseId,
      questions: latestResponse,
      selectedAnswers: latestSelectedAnswers,
    });

    if (!payload.courseId || payload.questionArray.length === 0) {
      return;
    }

    const authToken = localStorage.getItem(LMS_TOKEN);

    fetch(`${LMS_BASE_URL}/course/pre-assessment`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
      },
      body: JSON.stringify(payload),
    }).catch(() => {});
  }, []);
  

  useEffect(() => {
    // Add a dummy history entry
    window.history.pushState(
      { guard: true },
      "",
      `${window.location.pathname}?${searchParams.toString()}`,
    );

    const handlePopState = () => {
      if (!allowLeave) {
        setShowWarning(true);

        // Re-push state to stop navigation
        window.history.pushState({ guard: true }, "", window.location.pathname);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [allowLeave]);

  const handleQuestionAnswer = useCallback((question, option) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [question?.assQuestionId]: {
        courseId: courseId,
        languageId: languageid,
        assessmentId: question?.assessmentId,
        assessmentQuestionId: question?.assQuestionId,
        assessmentAnswerId: option?.assAnswerId,
      },
    }));
  }, []);

  useEffect(() => {
    // if (!showTimer) return;
    if (secondsLeft <= 0) {
      handleAutoSubmit?.();
      return;
    }

    const timer = setInterval(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [secondsLeft]);

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const seconds = String(secondsLeft % 60).padStart(2, "0");

  const isDanger = secondsLeft <= 30;

  const isLastQuestion = response && activeIndex === response?.length - 1;
  const currentQuestionId = response?.[activeIndex]?.assQuestionId;
  const selectedAnswer = selectedAnswers[currentQuestionId] || null;
  const isOptionSelected = Boolean(selectedAnswer);

  const handleBack = () => {
    setShowWarning(true);
  };

  const handleContinueAssessment = () => {
    setShowWarning(false);
    setAllowLeave(false);
  };

  useEffect(() => {
    // Add a dummy history entry
    window.history.pushState(
      { guard: true },
      "",
      `${window.location.pathname}?${searchParams.toString()}`,
    );

    const handlePopState = () => {
      if (!allowLeave) {
        setShowWarning(true);

        // Re-push state to stop navigation
        window.history.pushState({ guard: true }, "", window.location.pathname);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [allowLeave]);

useEffect(() => {
  const allowed = sessionStorage.getItem(ASSESSMENT_ALLOWED);

  if (!allowed) {
    navigate(
      `/user/module-list?courseId=${courseId}&courseName=${courseName}`,
      { replace: true }
    );
    return;
  }

  // Submit answered questions and remove flag when tab/page unloads.
  const handleBeforeUnload = () => {
    // submitAssessmentOnRefresh();
    sessionStorage.removeItem(ASSESSMENT_ALLOWED);
  };

  window.addEventListener("beforeunload", handleBeforeUnload);

  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
}, [courseId, courseName, navigate, submitAssessmentOnRefresh]);

  useEffect(() => {
        const allowed = sessionStorage.getItem(ASSESSMENT_ALLOWED);
      
        if (!allowed) {
          navigate(
            `/user/module-list?courseId=${courseId}&courseName=${courseName}`,
            {replace:true}
          );
        }
  }, []);
  
  return (
    <div className={styles.page}>
      <HeaderModule
        title="Pre-Course Assessment"
        className={styles["header-title"]}
        minutes={minutes}
        seconds={seconds}
        isDanger={isDanger}
        onBackClick={handleBack}
      />
      <div className={styles["container-wrapper"]}>
        <div className={styles["content-part"]}>
          <p className={styles.title}>{courseName}</p>
          <ProgressBar current={activeIndex} total={response?.length} />
          {isLoading ? (
            <Shimmer width="90%" height="324px" borderRadius="14px" />
          ) : (
            <QuestionCards
              selectedAnswer={selectedAnswer}
              questionsList={response}
              handleQuestionAnswer={handleQuestionAnswer}
              activeIndex={activeIndex}
              animate={animate}
            />
          )}
        </div>
      </div>
      <div className={styles["bottom-wrapper"]}>
        <Button
          label='Previous'
          buttonVariant={BUTTON_VARIANTS.SECONDARY}
          className={styles["button-class"]}
          onClick={handlePreviousClick}
          disabled={activeIndex === 0 || activeIndex < 0 || isPending}
        />
        <Button
          loading={isPending}
          label={
            isLastQuestion
              ? isPending
                ? "Submitting..."
                : "Submit"
              : isPending
                ? "Submitting..."
                : "Next"
          }
          buttonVariant={BUTTON_VARIANTS.PRIMARY}
          className={styles["button-class"]}
          onClick={handleSubmit}
          disabled={!isOptionSelected || isPending}
        />
      </div>

      {showSubmitModal && (
        <div className={styles["submit-modal-overlay"]}>
          <div className={styles["submit-modal"]}>
            <h2 className={styles["submit-modal-title"]}>
              Ready to Submit?
            </h2>
            <p className={styles["submit-modal-subtitle"]}>
              Please review your answers or submit the assessment.
            </p>
            <div className={styles["submit-modal-actions"]}>
              <Button
                label="Review"
                buttonVariant={BUTTON_VARIANTS.TERNARY}
                className={styles["modal-button"]}
                onClick={handleReview}
                disabled={isPending}
              />
              <Button
                label={isPending ? "Submitting..." : "Submit"}
                buttonVariant={BUTTON_VARIANTS.PRIMARY}
                className={styles["modal-button"]}
                onClick={handleFinalSubmit}
                disabled={isPending}
              />
            </div>
          </div>
        </div>
      )}

      {showWarning && (
        <MandatoryAssessmentModal
          handleContinueAssessment={handleContinueAssessment}
          show={showWarning}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default PreCourseAssessment;
